import { BookMarked, Gauge } from "lucide-react"
import { useEffect } from "react"
import { useNavigate, useParams } from "react-router"

import { CatalogoCumplimiento } from "@/componentes/cumplimiento/CatalogoCumplimiento"
import { TableroCumplimiento } from "@/componentes/cumplimiento/TableroCumplimiento"
import { Pestanas } from "@/componentes/ui/Pestanas"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

export const VISTAS = [
  { valor: "tablero", etiqueta: "Tablero", icono: Gauge, permiso: "cumplimiento:ver" },
  {
    valor: "catalogo",
    etiqueta: "Entes y obligaciones",
    icono: BookMarked,
    permiso: "cumplimiento:administrar",
  },
] as const

type Vista = (typeof VISTAS)[number]["valor"]

/**
 * Cumplimiento ante entes gubernamentales y parafiscales (CUMP-1): qué se le debe
 * presentar a cada ente, lo que ya se presentó con su soporte y lo que está por vencer.
 *
 * ⚠ **No mueve dinero.** Si una obligación se paga, el pago sigue siendo una orden de
 * pago con sus tres firmas; aquí se registra que se presentó y hasta cuándo vale.
 */
export function Cumplimiento() {
  const usuario = usarSesion((e) => e.usuario)
  const { vista: enRuta } = useParams()
  const navegar = useNavigate()
  const visibles = VISTAS.filter((v) => tienePermiso(usuario, v.permiso))
  const vista: Vista | undefined =
    visibles.find((v) => v.valor === enRuta)?.valor ?? visibles[0]?.valor

  useEffect(() => {
    if (vista !== undefined && enRuta !== vista) {
      navegar(`/cumplimiento/${vista}`, { replace: true })
    }
  }, [enRuta, vista, navegar])

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-xl font-semibold">Entes y parafiscales</h1>
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Solvencias, declaraciones y registros ante SENIAT, IVSS, FAOV, INCES, la alcaldía y demás
          entes: qué está vigente y qué está por vencer.
        </p>
      </header>
      {vista !== undefined && (
        <Pestanas
          pestanas={visibles}
          activa={vista}
          onCambiar={(v) => navegar(`/cumplimiento/${v}`, { replace: true })}
          etiqueta="Vistas de cumplimiento"
          idPanel="panel-cumplimiento"
          prefijoId="pestana-cumplimiento"
        />
      )}
      <div
        id="panel-cumplimiento"
        role="tabpanel"
        aria-labelledby={`pestana-cumplimiento-${vista}`}
        className="mt-4"
      >
        {vista === "tablero" && <TableroCumplimiento />}
        {vista === "catalogo" && <CatalogoCumplimiento />}
      </div>
    </div>
  )
}
